import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const EditUserModal = ({ isOpen, onClose, user, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    role: 'student',
    status: 'active'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user?.name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        city: user?.city || '',
        state: user?.state || '',
        role: user?.role || 'student',
        status: user?.status || 'active'
      });
    }
    // Reset password success message when modal opens
    setPasswordResetSuccess(false);
  }, [user]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);

    // Validation
    if (!formData?.name?.trim()) {
      setError('Name is required');
      return;
    }
    if (!formData?.email?.trim()) {
      setError('Email is required');
      return;
    }
    if (!formData?.phone?.trim()) {
      setError('Phone is required');
      return;
    }

    setLoading(true);
    try {
      await onSave(user?.id, formData);
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!confirm(`Are you sure you want to reset the password for ${user?.email}? A temporary password will be generated and sent to their email.`)) {
      return;
    }

    setResettingPassword(true);
    setError(null);
    setPasswordResetSuccess(false);

    try {
      const { resetPassword } = await import('../../../services/adminService');
      await resetPassword(user?.id, user?.email);
      setPasswordResetSuccess(true);
      setTimeout(() => setPasswordResetSuccess(false), 5000);
    } catch (err) {
      setError(err?.message || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const roleOptions = [
    { value: 'student', label: 'Student' },
    { value: 'institute', label: 'Institute' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'pending', label: 'Pending' },
    { value: 'suspended', label: 'Suspended' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="UserCog" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">Edit User</h2>
              <p className="text-sm text-muted-foreground">Update user information</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            disabled={loading}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg">
              <Icon name="AlertCircle" size={16} color="var(--color-error)" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          {passwordResetSuccess && (
            <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/20 rounded-lg">
              <Icon name="CheckCircle" size={16} color="var(--color-success)" />
              <p className="text-sm text-success">Password reset successfully! Temporary password sent to user's email.</p>
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Full Name *
            </label>
            <Input
              type="text"
              placeholder="Enter full name"
              value={formData?.name}
              onChange={(e) => handleChange('name', e?.target?.value)}
              disabled={loading}
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Email *
            </label>
            <Input
              type="email"
              placeholder="Enter email address"
              value={formData?.email}
              onChange={(e) => handleChange('email', e?.target?.value)}
              disabled={loading}
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Phone Number *
            </label>
            <Input
              type="tel"
              placeholder="Enter phone number"
              value={formData?.phone}
              onChange={(e) => handleChange('phone', e?.target?.value)}
              disabled={loading}
            />
          </div>

          {/* City and State */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                City
              </label>
              <Input
                type="text"
                placeholder="Enter city"
                value={formData?.city}
                onChange={(e) => handleChange('city', e?.target?.value)}
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                State
              </label>
              <Input
                type="text"
                placeholder="Enter state"
                value={formData?.state}
                onChange={(e) => handleChange('state', e?.target?.value)}
                disabled={loading}
              />
            </div>
          </div>

          {/* Role and Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Role
              </label>
              <Select
                options={roleOptions}
                value={formData?.role}
                onChange={(value) => handleChange('role', value)}
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Status
              </label>
              <Select
                options={statusOptions}
                value={formData?.status}
                onChange={(value) => handleChange('status', value)}
                disabled={loading}
              />
            </div>
          </div>

          {/* Password Management Section */}
          <div className="p-4 bg-muted/30 border border-border rounded-lg">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="Lock" size={16} color="var(--color-primary)" />
                  <h3 className="text-sm font-semibold text-foreground">Password Management</h3>
                </div>
                <p className="text-xs text-muted-foreground">
                  Reset user password and send temporary credentials to their email
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleResetPassword}
                disabled={loading || resettingPassword}
              >
                {resettingPassword ? (
                  <>
                    <Icon name="Loader2" size={14} className="animate-spin" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <Icon name="KeyRound" size={14} />
                    Reset Password
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Icon name="Save" size={16} />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal;